/**
 * T048 (catalogue-sku-editor). Structural pattern copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout, and the picker idiom this screen needs) — the
 * same sources ../brands/New.tsx and ../products/New.tsx cite
 * (specs/017-isomorphic-theme-only/contracts/screen-map.md).
 *
 * Not built on `@steelyard/theme`'s `<Form>` wrapper like its siblings: this screen's product
 * picker is a brand→category→product cascade, which needs to react to two watched fields
 * (`watch`, `useEffect`) from the component that owns them. `<Form>` creates its own internal
 * `useForm()` and only exposes it through a render-prop callback — calling `useEffect` inside that
 * callback would call a hook from a plain nested function, not this component's own body, which
 * `react-hooks/rules-of-hooks` correctly rejects. `<Form>` is exactly this: `useForm` +
 * `zodResolver` + a plain `<form noValidate>`, inlined here so the cascade can live in `NewSku`
 * itself.
 *
 * Scope correction vs. the task's own prose: FR-009's "brand, category, ... images" are
 * `ProductView`/`CreateProductRequest` fields, not `CreateSkuRequest`'s — confirmed against
 * `catalog.d.ts` and `CatalogueController.cs`'s `CreateSkuRequest` record, which carries no
 * brand/category/image member at all. A SKU always belongs to an existing product; this screen
 * captures product (via the cascade), code, name, HSN, GST rate, attributes and stocking unit —
 * never brand/category/images directly. Product creation is T046, not this group. No
 * `primaryUnitId` field — it is a property of the unit set, set on the Units screen (T051), not at
 * creation (FR-009).
 */
import { useEffect, useState } from "react";
import { useLoaderData, useNavigate, useOutletContext } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiIdentificationBadgeDuotone as IdentityIcon, PiPackageDuotone as ProductIcon, PiSlidersHorizontalDuotone as AttributesIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import {
  createSku,
  listBrands,
  listCategoryTree,
  listGstRates,
  listProducts,
  listUnits,
  type Brand,
  type Category,
  type GstRate,
  type Product,
  type UnitOption,
} from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { useGoBack } from "../../../lib/navigation";
import { skuPath } from "../../../lib/paths";

/**
 * Active brands, categories, products and GST rates only (project owner, 2026-09-29) — a new SKU is
 * new work, and new work is not filed under something switched off. `listUnits()` has no query
 * parameters and units carry no active flag — it always returns the whole reference table.
 */
export async function newSkuLoader() {
  const [brands, categories, units, gstRates] = await Promise.all([
    listBrands(1, true, 200),
    listCategoryTree(true),
    listUnits(),
    listGstRates(true),
  ]);
  if (!brands || !categories || !units || !gstRates) throw new Error("The API returned no body for the SKU form's pickers.");
  return { brands, categories, units, gstRates };
}

const skuSchema = z.object({
  brandId: z.string().trim().min(1, "Required"),
  categoryId: z.string().trim().min(1, "Required"),
  productId: z.string().trim().min(1, "Choose a product"),
  code: z.string().trim().min(1, "Required").max(50),
  name: z.string().trim().min(1, "Required").max(200),
  hsnCode: z.string().trim().min(1, "Required").max(20),
  // BUG-08: Catalog now owns a GstRate registry (GET /api/catalog/gst-rates) — a real dropdown
  // replaces the free-text id this screen used to accept unvalidated.
  gstRateId: z.string().trim().min(1, "Choose a GST rate"),
  stockingUnitId: z.string().trim().min(1, "Required"),
  size: z.string().trim().max(50).optional().or(z.literal("")),
  thickness: z.string().trim().max(50).optional().or(z.literal("")),
  grade: z.string().trim().max(50).optional().or(z.literal("")),
  finish: z.string().trim().max(50).optional().or(z.literal("")),
  length: z.string().trim().max(50).optional().or(z.literal("")),
});
type SkuFormValues = z.infer<typeof skuSchema>;

const defaultValues: SkuFormValues = {
  brandId: "",
  categoryId: "",
  productId: "",
  code: "",
  name: "",
  hsnCode: "",
  gstRateId: "",
  stockingUnitId: "",
  size: "",
  thickness: "",
  grade: "",
  finish: "",
  length: "",
};

function attributesOrNull(values: SkuFormValues) {
  const attrs = {
    size: values.size || null,
    thickness: values.thickness || null,
    grade: values.grade || null,
    finish: values.finish || null,
    length: values.length || null,
  };
  return Object.values(attrs).some((v) => v !== null) ? attrs : null;
}

/** Ported from the stale apps/admin/app/catalogue/skus/new/page.tsx path in tasks.md T048. */
export default function NewSku() {
  const navigate = useNavigate();
  const goBack = useGoBack("/catalogue/skus");
  const { brands, categories, units, gstRates } = useLoaderData() as Awaited<ReturnType<typeof newSkuLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<unknown>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SkuFormValues>({ resolver: zodResolver(skuSchema), defaultValues });

  const brandId = watch("brandId");
  const categoryId = watch("categoryId");

  // Degrades past 100 products in one brand+category (`hint` below states this) rather than
  // building an unrequested search combobox — `GET /products` takes no `q` parameter.
  useEffect(() => {
    if (!brandId || !categoryId) {
      setProducts(null);
      return;
    }
    let cancelled = false;
    setProductsLoading(true);
    setProductsError(null);
    setValue("productId", "");
    listProducts({ page: 1, activeOnly: true, brandId, categoryId, pageSize: 100 })
      .then((result) => {
        if (!cancelled) setProducts(result.items);
      })
      .catch((err) => {
        // Constitution VI: a failed fetch is never swallowed into "no products" — an empty result
        // and a broken request read identically to a user unless the failure is shown.
        if (!cancelled) {
          setProducts(null);
          setProductsError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, categoryId, setValue]);

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="create a SKU" />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const { id } = await createSku({
        productId: values.productId,
        code: values.code,
        name: values.name,
        hsnCode: values.hsnCode,
        gstRateId: values.gstRateId,
        stockingUnitId: values.stockingUnitId,
        attributes: attributesOrNull(values),
      });
      toast.success("SKU created");
      navigate(skuPath(id), { replace: true }); // the id URL redirects to the slug one
    } catch (err) {
      // DEF-06: a toast alone was missing on a fast rejection (a 409 duplicate code, most
      // often — it can resolve almost immediately). This surfaces the same failure as a
      // message that stays on the page until the next submit.
      setSubmitError(err);
      toast.error(problemTitle(err, "Could not create the SKU"));
    }
  });

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="New SKU"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue · SKUs", href: "/catalogue/skus" }, { name: "New SKU" }]}
      />

      {productsError ? <ErrorSurface error={productsError} action="load products for this brand and category" /> : null}
      {submitError ? <ErrorSurface error={submitError} action="create this SKU" /> : null}

      <form noValidate onSubmit={onSubmit} className={formCard}>
        <div className={formSections}>
          <FormGroup title={<SectionTitle icon={ProductIcon}>Product</SectionTitle>} description="A SKU always belongs to an existing product — narrow by brand and category to find it.">
            <SelectField
              label="Brand"
              required
              error={errors.brandId?.message}
              disabled={brands.items.length === 0}
              disabledReason={brands.items.length === 0 ? "No brands available." : undefined}
              {...register("brandId")}
            >
              <option value="">{brands.items.length === 0 ? "No brands available" : "Choose a brand…"}</option>
              {brands.items.map((b: Brand) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Category"
              required
              error={errors.categoryId?.message}
              disabled={categories.items.length === 0}
              disabledReason={categories.items.length === 0 ? "No categories available." : undefined}
              {...register("categoryId")}
            >
              <option value="">{categories.items.length === 0 ? "No categories available" : "Choose a category…"}</option>
              {categories.items.map((c: Category) => (
                <option key={c.id} value={c.id}>
                  {"  ".repeat(c.depth)}
                  {c.name}
                </option>
              ))}
            </SelectField>
            {/* Three states beyond the normal one: still loading (falls through to the `hint`
                below, disabled with no disabledReason so the hint's "Loading…" shows through),
                a parent not yet chosen, and a parent chosen with genuinely zero products. */}
            <SelectField
              label="Product"
              required
              disabled={!brandId || !categoryId || productsLoading || (products !== null && products.length === 0 && !productsLoading)}
             
              disabledReason={
                !brandId || !categoryId
                  ? "Choose a brand and category first."
                  : !productsLoading && products !== null && products.length === 0
                    ? "No products available for this brand and category."
                    : undefined
              }
              hint={
                productsLoading
                  ? "Loading…"
                  : products && products.length >= 100
                    ? "Showing the first 100 products for this brand and category."
                    : undefined
              }
              error={errors.productId?.message}
              {...register("productId")}
            >
              <option value="">
                {!productsLoading && products !== null && products.length === 0 ? "No products available" : products ? "Choose a product…" : "—"}
              </option>
              {(products ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>

          <FormGroup title={<SectionTitle icon={IdentityIcon}>Identity</SectionTitle>} description="Code is permanent once saved.">
            <Field label="Code" required maxLength={50} autoComplete="off" className="font-mono" error={errors.code?.message} {...register("code")} />
            <Field label="Name" required maxLength={200} autoComplete="off" error={errors.name?.message} {...register("name")} />
            <Field label="HSN code" required maxLength={20} autoComplete="off" className="font-mono" error={errors.hsnCode?.message} {...register("hsnCode")} />
            <SelectField
              label="GST rate"
              required
              disabled={gstRates.items.length === 0}
              disabledReason={gstRates.items.length === 0 ? "No GST rates available — add one under Catalogue › GST rates." : undefined}
              error={errors.gstRateId?.message}
              {...register("gstRateId")}
            >
              <option value="">{gstRates.items.length === 0 ? "No GST rates available" : "Choose a GST rate…"}</option>
              {gstRates.items.map((r: GstRate) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Stocking unit"
              required
             
              disabled={units.items.length === 0}
              disabledReason={units.items.length === 0 ? "No units available." : undefined}
              error={errors.stockingUnitId?.message}
              {...register("stockingUnitId")}
            >
              <option value="">{units.items.length === 0 ? "No units available" : "Choose a unit…"}</option>
              {units.items.map((u: UnitOption) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>

          <FormGroup title={<SectionTitle icon={AttributesIcon}>Attributes</SectionTitle>} description="Optional — whatever applies to this SKU.">
            <Field label="Size" maxLength={50} error={errors.size?.message} {...register("size")} />
            <Field label="Thickness" maxLength={50} error={errors.thickness?.message} {...register("thickness")} />
            <Field label="Grade" maxLength={50} error={errors.grade?.message} {...register("grade")} />
            <Field label="Finish" maxLength={50} error={errors.finish?.message} {...register("finish")} />
            <Field label="Length" maxLength={50} error={errors.length?.message} {...register("length")} />
          </FormGroup>
        </div>

        <FormFooter
          className={formCardFooter}
          submitBtnText={isSubmitting ? "Creating…" : "Create SKU"}
          isLoading={isSubmitting}
          altBtnText="Cancel"
          handleAltBtn={goBack}
        />
      </form>
    </div>
  );
}
